import styles from "./SpeedChartStatsHeader.module.scss"
import BigNumber from "bignumber.js"
import { ReactNode, useRef, useState } from "react"
import { getSpeedDiffData } from "../getSpeedChartOptionsHelpers"

export interface SpeedChartStatsHeaderProps {
  isLaden: boolean
  months: number
  min: number
  max: number
  avg: number
  seaMarginFraction: number
  isManualOverride: boolean
  descSpeed: number
  isShowSeaMarginChecked: boolean
  onMarginChange: (fraction: number) => void
  onMarginReset: () => void
  statsExtra?: ReactNode
}

function getSmColor(pct: number): string {
  if (pct > 10) return "red"
  if (pct > 6) return "orange"
  return "green"
}

export function SpeedChartStatsHeader({
  isLaden,
  months,
  min,
  max,
  avg,
  seaMarginFraction,
  isManualOverride,
  descSpeed,
  isShowSeaMarginChecked,
  onMarginChange,
  onMarginReset,
  statsExtra,
}: SpeedChartStatsHeaderProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState("")
  const [isHovered, setIsHovered] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const smPct = Number((seaMarginFraction * 100).toFixed(1))
  const smAdjustedAvg = BigNumber(avg * (1 + seaMarginFraction))
    .decimalPlaces(1)
    .toNumber()
  const displayAvg = BigNumber(avg).decimalPlaces(1).toNumber()

  const diffSource = isShowSeaMarginChecked ? smAdjustedAvg : avg
  const diffData = getSpeedDiffData(diffSource, descSpeed)

  const smColor = getSmColor(smPct)
  const pillLabel = isManualOverride ? "Manual SM Adjusted" : "SM Adjusted"

  const handleClick = () => {
    setEditValue(smPct.toString())
    setIsEditing(true)
    setIsHovered(false)
    setTimeout(() => inputRef.current?.select(), 0)
  }

  const commitEdit = () => {
    const parsed = parseFloat(editValue)
    if (!isNaN(parsed) && parsed >= 0 && parsed <= 100) {
      const newFraction = parsed / 100
      if (Number(parsed.toFixed(1)) !== Number(smPct.toFixed(1))) {
        onMarginChange(newFraction)
      }
    }
    setIsEditing(false)
  }

  const cancelEdit = () => {
    setIsEditing(false)
  }

  return (
    <div
      style={{
        fontFamily: "'Source Sans Pro', sans-serif",
        fontSize: 14,
        fontWeight: 400,
        lineHeight: "20px",
        padding: "4px 0 0 8px",
        userSelect: "none",
      }}
    >
      {/* Line 1: Title + diff badge + SM pill */}
      <div className={styles.titleRow}>
        <span style={{ fontWeight: 600 }}>
          {isLaden ? "LADEN" : "BALLAST"} (last {months} months)
        </span>
        {diffData && (
          <span style={{ color: diffData.color, marginLeft: 12 }}>
            {diffData.text}
          </span>
        )}
        {isShowSeaMarginChecked && (
          <span
            style={{
              backgroundColor: "#3050a2",
              color: "#fff",
              padding: "2px 8px",
              borderRadius: 12,
              fontSize: 10,
              lineHeight: "16px",
              marginLeft: 4,
            }}
          >
            {pillLabel}
          </span>
        )}
        {isManualOverride && isShowSeaMarginChecked && (
          <span
            onClick={onMarginReset}
            style={{
              fontSize: 11,
              color: "#3050a2",
              cursor: "pointer",
              marginLeft: 4,
              textDecoration: "underline",
            }}
          >
            Reset
          </span>
        )}
      </div>

      {/* Line 2: Stats */}
      <div className={styles.statsRow}>
        <span>
          Min: <strong style={{ marginRight: 16 }}>{min}</strong>
          Max: <strong style={{ marginRight: 16 }}>{max}</strong>
          Avg: <strong>{displayAvg}</strong>
        </span>
        {isShowSeaMarginChecked && (
          <span style={{ marginLeft: 8, position: "relative" }}>
            SM Adjusted avg:{" "}
            <strong style={{ marginLeft: 4 }}>{smAdjustedAvg}</strong>
            {isEditing ? (
              <input
                ref={inputRef}
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitEdit()
                  if (e.key === "Escape") cancelEdit()
                }}
                onBlur={commitEdit}
                style={{
                  width: 56,
                  marginLeft: 4,
                  fontSize: 13,
                  padding: "1px 4px",
                  border: "1px solid #ccc",
                  borderRadius: 4,
                  outline: "none",
                }}
              />
            ) : (
              <span
                onClick={handleClick}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                style={{
                  color: smColor,
                  marginLeft: 4,
                  cursor: "pointer",
                  borderBottom: "1px dashed",
                  borderColor: smColor,
                  position: "relative",
                }}
              >
                ({smPct.toFixed(1)}%)
                {isHovered && (
                  <span
                    style={{
                      position: "absolute",
                      left: 0,
                      top: "100%",
                      whiteSpace: "nowrap",
                      fontSize: 11,
                      color: "#666",
                      fontWeight: 400,
                      pointerEvents: "none",
                    }}
                  >
                    Select to edit sea margin
                  </span>
                )}
              </span>
            )}
          </span>
        )}
        {statsExtra}
      </div>
    </div>
  )
}
